import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Holiday } from "@ckgo2/shared/types";
import { useEditPage } from "@/hooks/useEditPage";
import EditPageHeader from "@/components/common/EditPageHeader";
import HolidayWindowsEditor, { type HolidayWindowValue } from "./HolidayWindowsEditor";

interface HolidayFormData {
  name: string;
  slug: string;
  windows: HolidayWindowValue[];
  sortOrder: number;
  status: string;
}

export default function HolidayEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    HolidayFormData,
    Holiday
  >({
    resourceKey: "holiday",
    listKey: "holidays",
    apiPath: "/api/admin/holidays",
    // Slug se generuje z názvu (`slugSourceField` je ve výchozím stavu `title`, tady je to
    // `name`) — je to stabilní klíč, přes který se překlápějí legacy odkazy `?holiday=`.
    slugSourceField: "name",
    defaultValues: {
      name: "",
      slug: "",
      windows: [],
      sortOrder: 0,
      status: "published",
    },
    navigateTo: "/holidays",
    transformOnLoad: (h) => ({
      name: h.name || "",
      slug: h.slug || "",
      windows: Array.isArray(h.windows) ? h.windows : [],
      sortOrder: h.sortOrder || 0,
      status: h.status || "published",
    }),
    transformOnSave: (data) => ({
      ...data,
      // Zahazuje se jen ÚPLNĚ PRÁZDNÝ řádek — ten vznikne klikem na „Přidat okno"
      // a nic v sobě nenese. Rozepsané okno (vyplněná jedna strana) se posílá dál
      // a odmítne ho schéma: dřív se tiše zahodilo, stránka odnavigovala na seznam
      // a operátor přišel o zadané datum bez jediné hlášky. Na rozepsaný řádek
      // upozorňuje editor sám, ještě před odesláním.
      windows: (data.windows || []).filter((w) => w.from || w.to),
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/holidays"
        isNew={isNew}
        newTitle={t("content.holidays.form.newTitle")}
        editTitle={t("content.holidays.form.editTitle")}
        entityName={itemData?.name}
        formId="holiday-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        isSuccess={saveMutation.isSuccess}
      />

      <form
        id="holiday-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("common.fields.name")}</label>
              <input
                {...register("name", { required: t("content.holidays.form.nameRequired") })}
                className="input"
                placeholder={t("content.holidays.form.namePlaceholder")}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
            </div>

            <div>
              <label className="label">{t("common.fields.slug")}</label>
              <input {...register("slug")} className="input" />
              <p className="mt-1 text-sm text-gray-500">{t("content.holidays.form.slugHint")}</p>
            </div>
          </div>

          <div className="card space-y-3">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {t("content.holidays.windows.title")}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{t("content.holidays.windows.hint")}</p>
            </div>
            <Controller
              name="windows"
              control={control}
              render={({ field }) => (
                <HolidayWindowsEditor value={field.value ?? []} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("common.fields.sortOrder")}</label>
              <input
                type="number"
                {...register("sortOrder", { valueAsNumber: true })}
                className="input"
              />
              <p className="mt-1 text-sm text-gray-500">
                {t("content.holidays.form.sortOrderHint")}
              </p>
            </div>
            <div>
              <label className="label">{t("common.fields.status")}</label>
              <select {...register("status")} className="input">
                <option value="published">{t("common.status.published")}</option>
                <option value="draft">{t("common.status.draft")}</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">{t("content.holidays.form.statusHint")}</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
